import type React from "react"
import { useRef, useState } from "react"

import { useTranslation } from "react-i18next"
import { Button, Icon, Menu, Popup, Progress } from "semantic-ui-react"
import { userIsConnectedTo } from "simplydo/core/connections"
import { styled } from "styled-components"

// @ts-ignore
import { useAppSelector } from "store"

import { DropZone, type DropZoneConfigProps } from "./DropZone"
import { FileUploadWrapper, type FileUploadWrapperProps, type FileUploadWrapperRef } from "./FileUploadWrapper"
import type { DragFile, UploadState } from "./uploadUtils"

const ChooserPopup = styled(Popup)`
  max-width: 400px !important;
  .menu {
    max-width: 400px !important;
    min-width: 300px !important;
  }
`

export type FileUploaderProps = {
  popupProps?: any
  dropzoneProps?: DropZoneConfigProps
  style?: React.CSSProperties
} & Omit<FileUploadWrapperProps, "children" | "setUploadState" | "setDraggedOverFiles">

export const FileUploaderPopup = ({
  accept,
  forType,
  forId,
  onError,
  popupProps = {},
  dropzoneProps = {},
  onComplete,
  allowMultiple,
  onCompleteMultiple,
  maxMBFileSize = 100,
}: FileUploaderProps) => {
  const { t } = useTranslation()
  const uploadRef = useRef<FileUploadWrapperRef>(null)

  const [draggedOverFiles, setDraggedOverFiles] = useState<Array<DragFile>>([])
  const [uploadState, setUploadState] = useState<UploadState>({
    isUploading: false,
    uploadProgress: 0,
  })

  const user = useAppSelector((state) => state.user)
  const connected365 = userIsConnectedTo(user, "office365")

  const [open, setOpen] = useState(false)
  return (
    <FileUploadWrapper
      accept={accept}
      forType={forType}
      forId={forId}
      ref={uploadRef}
      setUploadState={setUploadState}
      setDraggedOverFiles={setDraggedOverFiles}
      maxMBFileSize={maxMBFileSize}
      onComplete={onComplete}
      allowMultiple={allowMultiple}
      onCompleteMultiple={onCompleteMultiple}
      onError={onError}
    >
      {connected365 ? (
        <ChooserPopup
          mountNode={document.getElementById("semantic-modal-mount-node")}
          style={{ zIndex: 1500 }}
          on="click"
          open={open}
          position="top center"
          onClose={() => setOpen(false)}
          onOpen={() => setOpen(true)}
          trigger={
            <div>
              <DropZone
                uploadState={uploadState}
                setUploadState={setUploadState}
                draggedOverFiles={draggedOverFiles}
                disabled={!!popupProps.disabled}
                allowMultiple={allowMultiple}
                accept={accept}
              />
            </div>
          }
          content={
            <div>
              <h5 style={{ marginTop: 0 }}>Upload a file</h5>
              <p style={{ color: "grey" }}>The maximum file size is {maxMBFileSize}MB.</p>
              <Menu vertical borderless>
                {uploadState.isUploading && (
                  <Progress percent={uploadState.uploadProgress} indicating progress style={{ margin: 5 }} />
                )}
                <Menu.Item
                  onClick={() => {
                    uploadRef.current.chooseFile()
                    setOpen(false)
                  }}
                >
                  <Icon name="upload" />
                  {t("ideas.actions.chooseFile")}
                </Menu.Item>
                {userIsConnectedTo(user, "office365") ? (
                  <Menu.Item
                    onClick={() => {
                      uploadRef.current.openOneDrive()
                      setOpen(false)
                    }}
                    icon="microsoft"
                    content="Choose from OneDrive"
                  />
                ) : null}
              </Menu>

              <div style={{ textAlign: "left", marginTop: 20 }}>
                <Button
                  basic
                  size="tiny"
                  content={t("generic.cancel")}
                  onClick={() => {
                    setOpen(false)
                  }}
                />
              </div>
            </div>
          }
          {...popupProps}
        />
      ) : (
        <DropZone
          uploadState={uploadState}
          setUploadState={setUploadState}
          draggedOverFiles={draggedOverFiles}
          disabled={!!popupProps.disabled}
          allowMultiple={allowMultiple}
          accept={accept}
          onClick={() => {
            uploadRef.current.chooseFile()
          }}
        />
      )}
    </FileUploadWrapper>
  )
}
